'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import ConditionHero from './ConditionHero';
import EducationalContent from './EducationalContent';
import ProductCard from './ProductCard';
import ComparisonTable from './ComparisonTable';
import TestimonialSection from './TestimonialSection';
import FAQSection from './FAQSection';
import MedicalDisclaimer from './MedicalDisclaimer';
import CTASection from './CTASection';
import ReviewsSection from './ReviewsSection';
import HealthVerticalMenu from '@/components/common/HealthVerticalMenu';

interface CategoryData {
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
}

interface ContentSection {
  id: string;
  title: string;
  content: string;
  icon: string;
}

interface SubscriptionTier {
  duration: string;
  price: number;
  savings?: string;
  popular?: boolean;
}

interface Product {
  id: string;
  sku: string;
  name: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
  ingredients: string[];
  doctorApproved: boolean;
  subscriptionTiers: SubscriptionTier[];
}

interface ComparisonFeature {
  feature: string;
  basic: boolean | string;
  standard: boolean | string;
  premium: boolean | string;
}

interface Testimonial {
  id: string;
  name: string;
  age: number;
  condition: string;
  quote: string;
  imageUrl: string;
  imageAlt: string;
  rating: number;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

function HealthVerticalContent() {
  const searchParams = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);
  const [category, setCategory] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
    const categoryParam = searchParams.get('category');
    setCategory(categoryParam);
  }, [searchParams]);

  const categoryDataMap: Record<string, CategoryData> = {
    'mental-health': {
      title: 'Mental Health & Wellness Solutions',
      description: 'Evidence-based treatments for anxiety, depression, and stress management. Our licensed psychiatrists provide personalized care plans tailored to your unique mental health journey.',
      imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1996cc89d-1766900301720.png",
      imageAlt: 'Peaceful woman with closed eyes meditating in bright natural light wearing white clothing'
    },
    'chronic-conditions': {
      title: 'Chronic Condition Management',
      description: 'Comprehensive care for diabetes, hypertension, and heart health. Our specialists help you manage your condition with ongoing support and medication management.',
      imageUrl: "https://images.unsplash.com/photo-1666887360819-8b1209e802f8",
      imageAlt: 'Senior man checking blood pressure with digital monitor at home healthcare setting'
    },
    'womens-health': {
      title: "Women's Health & Reproductive Care",
      description: 'Specialized care for reproductive health, pregnancy support, and hormonal balance. Our women\'s health experts provide compassionate, comprehensive treatment options.',
      imageUrl: "https://images.unsplash.com/photo-1595684595530-095b61d7def9",
      imageAlt: 'Pregnant woman in white dress holding belly outdoors in natural sunlight'
    },
    'pediatric-care': {
      title: 'Pediatric Care & Child Health',
      description: 'Expert care for your child\'s health, development, and vaccination needs. Our pediatricians ensure your child receives the best possible care at every stage.',
      imageUrl: "https://images.unsplash.com/photo-1585842378055-55e2ede0c7c0",
      imageAlt: 'Happy young child smiling during pediatric checkup with stethoscope examination'
    },
    'dermatology': {
      title: 'Dermatology & Skin Health',
      description: 'Professional treatment for acne, eczema, and various skin conditions. Our dermatologists provide personalized skincare solutions for healthy, radiant skin.',
      imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_143835b43-1767951777624.png",
      imageAlt: 'Close-up of clear healthy skin with natural glow and smooth texture'
    },
    'nutrition': {
      title: 'Nutrition & Weight Management',
      description: 'Science-backed nutrition plans and weight management programs. Our registered dietitians help you achieve your health goals through sustainable lifestyle changes.',
      imageUrl: "https://images.unsplash.com/photo-1631091877628-9eba5229e8c5",
      imageAlt: 'Fresh colorful vegetables and fruits arranged on wooden cutting board for healthy meal prep'
    }
  };

  const educationalSections: ContentSection[] = [
  {
    id: '1',
    title: 'Understanding the Condition',
    content: 'This condition affects millions of people worldwide and can significantly impact daily life. Understanding the underlying causes, symptoms, and triggers is the first step toward effective management and improved quality of life.',
    icon: 'BookOpenIcon'
  },
  {
    id: '2',
    title: 'Common Symptoms',
    content: 'Symptoms can vary from person to person but typically include physical, emotional, and behavioral changes. Early recognition of symptoms allows for timely intervention and better treatment outcomes.',
    icon: 'ClipboardDocumentListIcon'
  },
  {
    id: '3',
    title: 'Treatment Approaches',
    content: 'Modern treatment combines medication, lifestyle modifications, and ongoing support. Our evidence-based approach ensures you receive comprehensive care tailored to your specific needs and health goals.',
    icon: 'BeakerIcon'
  },
  {
    id: '4',
    title: 'Long-term Management',
    content: 'Successful management requires consistent treatment adherence and regular follow-ups. Our platform provides continuous support, medication refills, and access to healthcare professionals whenever you need guidance.',
    icon: 'ChartBarIcon'
  }];


  const mockProducts: Product[] = [
  {
    id: '1',
    sku: 'MH-001-2024',
    name: 'Anxiety Relief Formula',
    description: 'Clinically proven medication for managing anxiety symptoms with minimal side effects. Prescribed by licensed psychiatrists after thorough evaluation.',
    imageUrl: "https://images.unsplash.com/photo-1679592180719-6d5124363e97",
    imageAlt: 'White prescription medication bottle with blue pills on clean medical background',
    ingredients: ['Sertraline', 'Vitamin B Complex', 'Magnesium'],
    doctorApproved: true,
    subscriptionTiers: [
    { duration: '1 Month Supply', price: 89, savings: undefined, popular: false },
    { duration: '3 Month Supply', price: 79, savings: 'Save 11%', popular: true },
    { duration: '6 Month Supply', price: 69, savings: 'Save 22%', popular: false }]

  },
  {
    id: '2',
    sku: 'MH-002-2024',
    name: 'Depression Management Kit',
    description: 'Comprehensive treatment package including medication and wellness supplements to support your mental health journey.',
    imageUrl: "https://images.unsplash.com/photo-1711662943976-acf10905bed1",
    imageAlt: 'Medical kit with various prescription bottles and wellness supplements arranged neatly',
    ingredients: ['Escitalopram', 'Omega-3', 'Vitamin D3'],
    doctorApproved: true,
    subscriptionTiers: [
    { duration: '1 Month Supply', price: 99, savings: undefined, popular: false },
    { duration: '3 Month Supply', price: 89, savings: 'Save 10%', popular: true },
    { duration: '6 Month Supply', price: 79, savings: 'Save 20%', popular: false }]

  },
  {
    id: '3',
    sku: 'MH-003-2024',
    name: 'Sleep & Stress Support',
    description: 'Natural and prescription-grade combination to improve sleep quality and reduce stress levels for better overall wellness.',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1e387312d-1768474934082.png",
    imageAlt: 'Blue and white medication capsules with sleep support supplements on white surface',
    ingredients: ['Melatonin', 'L-Theanine', 'Ashwagandha'],
    doctorApproved: true,
    subscriptionTiers: [
    { duration: '1 Month Supply', price: 69, savings: undefined, popular: false },
    { duration: '3 Month Supply', price: 59, savings: 'Save 14%', popular: true },
    { duration: '6 Month Supply', price: 49, savings: 'Save 29%', popular: false }]

  }];


  const comparisonFeatures: ComparisonFeature[] = [
  { feature: 'Monthly Medication Supply', basic: true, standard: true, premium: true },
  { feature: 'Licensed Physician Consultation', basic: true, standard: true, premium: true },
  { feature: 'Free Shipping', basic: false, standard: true, premium: true },
  { feature: 'Priority Support', basic: false, standard: false, premium: true },
  { feature: 'Wellness Supplements', basic: false, standard: true, premium: true },
  { feature: 'Monthly Check-ins', basic: '1', standard: '2', premium: '4' },
  { feature: 'Prescription Refills', basic: 'Manual', standard: 'Automatic', premium: 'Automatic' },
  { feature: 'Cost per Month', basic: '$89', standard: '$79', premium: '$69' }];


  const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Sarah Johnson',
    age: 32,
    condition: 'Anxiety',
    quote: 'This platform changed my life. The convenience of online consultations and home delivery made managing my anxiety so much easier. I feel supported every step of the way.',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_13fd90a86-1763294635574.png",
    imageAlt: 'Professional woman with brown hair smiling warmly at camera in natural lighting',
    rating: 5
  },
  {
    id: '2',
    name: 'Michael Chen',
    age: 45,
    condition: 'Depression',
    quote: 'The doctors are incredibly professional and caring. Having access to quality mental healthcare from home has been a game-changer for my treatment journey.',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1d8aed28d-1763294966598.png",
    imageAlt: 'Asian man in casual blue shirt with friendly smile in outdoor setting',
    rating: 5
  },
  {
    id: '3',
    name: 'Emily Rodriguez',
    age: 28,
    condition: 'Stress Management',
    quote: 'I was skeptical about online healthcare, but the thorough evaluation and ongoing support exceeded my expectations. My stress levels have improved significantly.',
    imageUrl: "https://img.rocket.new/generatedImages/rocket_gen_img_1345fbf99-1763293684642.png",
    imageAlt: 'Young Hispanic woman with long dark hair smiling confidently in bright daylight',
    rating: 5
  }];


  const faqs: FAQ[] = [
  {
    id: '1',
    question: 'How does the online consultation process work?',
    answer: 'After creating an account, you\'ll complete a comprehensive medical questionnaire. A licensed physician will review your information within 24 hours and determine if treatment is appropriate. If approved, your prescription will be sent to our pharmacy and shipped directly to your door.'
  },
  {
    id: '2',
    question: 'Are the medications FDA-approved?',
    answer: 'Yes, all medications prescribed through our platform are FDA-approved and sourced from licensed US pharmacies. Our physicians only prescribe evidence-based treatments with proven safety and efficacy profiles.'
  },
  {
    id: '3',
    question: 'What if I need to adjust my treatment?',
    answer: 'You can message your healthcare provider anytime through our secure platform. If adjustments are needed, your doctor will review your case and modify your treatment plan accordingly. Follow-up consultations are included in your subscription.'
  },
  {
    id: '4',
    question: 'Is my health information secure?',
    answer: 'Absolutely. We are HIPAA-compliant and use bank-level encryption to protect your personal health information. Your data is never shared with third parties without your explicit consent.'
  },
  {
    id: '5',
    question: 'Can I cancel my subscription anytime?',
    answer: 'Yes, you can cancel your subscription at any time through your account dashboard. There are no cancellation fees, and you\'ll continue to have access to your treatment through the end of your current billing period.'
  },
  {
    id: '6',
    question: 'Do you accept insurance?',
    answer: 'Currently, we do not accept insurance. However, our transparent pricing is often comparable to or less than insurance co-pays. We provide itemized receipts that you can submit to your insurance for potential reimbursement.'
  }];


  const currentCategory = categoryDataMap[category || 'mental-health'] || categoryDataMap['mental-health'];

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-20">
          <ConditionHero
            category={category || 'mental-health'}
            title={currentCategory.title}
            description={currentCategory.description}
            imageUrl={currentCategory.imageUrl}
            imageAlt={currentCategory.imageAlt} />

        </div>
      </div>);

  }

  // Show all health vertical options when no category is selected
  if (!category) {
    return (
      <div className="min-h-screen bg-background">
        <div className="pt-32 pb-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                  Health Solutions
                </h1>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Choose from our comprehensive range of specialized healthcare services. 
                  Select a category to explore treatments tailored to your needs.
                </p>
              </div>
              <HealthVerticalMenu variant="horizontal" showDescriptions={true} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-20">
        <ConditionHero
          category={category}
          title={currentCategory.title}
          description={currentCategory.description}
          imageUrl={currentCategory.imageUrl}
          imageAlt={currentCategory.imageAlt} />


        <EducationalContent sections={educationalSections} />

        <section data-section="products" className="py-16 lg:py-24 bg-muted/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-4 text-center">
                Recommended Treatment Options
              </h2>
              <p className="text-lg text-muted-foreground mb-12 text-center max-w-2xl mx-auto">
                Choose from our doctor-approved treatment plans designed specifically for your condition
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {mockProducts.map((product) =>
                <ProductCard key={product.id} product={product} />
                )}
              </div>
            </div>
          </div>
        </section>

        <div className="py-16 bg-background">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-heading font-bold text-center text-foreground mb-12">
              Recommended Products
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mockProducts.map((product) =>
              <ProductCard key={product.id} product={product} />
              )}
            </div>
          </div>
        </div>

        {mockProducts.length > 0 &&
        <ReviewsSection
          productId={mockProducts[0].sku}
          productName={mockProducts[0].name} />

        }

        <ComparisonTable features={comparisonFeatures} />

        <TestimonialSection testimonials={testimonials} />

        <FAQSection faqs={faqs} />

        <MedicalDisclaimer />

        <CTASection category={category} />
      </div>
    </div>);

}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="container mx-auto px-4 lg:px-8 py-20">
        <div className="animate-pulse space-y-8">
          <div className="h-12 bg-muted rounded-lg w-3/4 mx-auto"></div>
          <div className="h-6 bg-muted rounded-lg w-1/2 mx-auto"></div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[1, 2, 3].map((i) =>
            <div key={i} className="h-96 bg-muted rounded-xl"></div>
            )}
          </div>
        </div>
      </div>
    </div>);

}

export default function HealthVerticalInteractive() {
  return (
    <Suspense fallback={<LoadingSkeleton />}>
      <HealthVerticalContent />
    </Suspense>);

}